/**
 * What changed between two analysis configurations (HU-29).
 *
 * Ported from the legacy `configDiff.js` and translated. Two reasons it lives here rather than
 * being asked for:
 *
 * - Virtual Profiler sends the comparison as **raw dotted paths**. The readable names exist only
 *   in the old Vue 2 frontend, and a screen showing `engines.coherence.ideaCoincidenceThresholdPct`
 *   is a screen nobody reads.
 * - The flattening rules carry a judgement that is presentation, not domain: three subtrees are
 *   compared whole because diffing them key by key produces dozens of rows that say one thing.
 *
 * Pure, so the rules are tested rather than eyeballed in a browser.
 */

/**
 * The readable names, in Spanish.
 *
 * An unknown path falls back to itself — a parameter added upstream shows as its path rather
 * than disappearing from the diff, which is the failure that matters: a change nobody can see is
 * worse than one with an ugly name.
 */
const CONFIG_LABELS: Readonly<Record<string, string>> = Object.freeze({
  // Gestures.
  'engines.gestures.gestureThresholdMs': 'Umbral de gesto (ms)',
  'engines.gestures.episodeMaxGapMs': 'Silencio que cierra un episodio (ms)',
  'engines.gestures.aboveBaselineMultiplier': 'Multiplicador por encima de la línea base',
  'engines.gestures.belowBaselineMultiplier': 'Multiplicador por debajo de la línea base',
  'engines.gestures.baselineWindowSeconds': 'Ventana de línea base (s)',
  'engines.gestures.targetFps': 'FPS objetivo',
  'engines.gestures.sagemakerBatchSize': 'Tamaño de lote de SageMaker',
  'engines.gestures.sagemakerConcurrency': 'Concurrencia de SageMaker',
  'engines.gestures.sampledFramesJpegQuality': 'Calidad JPEG de los fotogramas muestreados',
  'engines.gestures.gestureCatalog': 'Catálogo de gestos',
  'engines.gestures.absoluteBands.rareMaxCount': 'Banda absoluta: máximo de «raro»',
  'engines.gestures.absoluteBands.intermediateMaxCount': 'Banda absoluta: máximo de «intermedio»',
  'engines.gestures.mlConfidenceThreshold': 'Confianza mínima del modelo facial',
  'engines.gestures.mlFramePersistence': 'Detalle guardado por fotograma',
  'engines.gestures.mlEpisodeSummary': 'Resumen de modelo por episodio',
  'engines.gestures.mlAlertDetail': 'Detalle de modelo en las alertas',
  // Coherence.
  'engines.coherence.semanticWindowSeconds': 'Ventana semántica (s)',
  'engines.coherence.bulkSize': 'Tamaño de tanda del LLM',
  // Retired with coherence engine 3.0.0; still named, because older versions carry them.
  'engines.coherence.segmentStrategy': 'Estrategia de segmentación (retirada)',
  'engines.coherence.singleIdeaAttribution': 'Atribución a una sola idea (retirada)',
  'engines.coherence.reductionByRule': 'Peso por regla (obsoleto)',
  'engines.coherence.semaphoreThresholdGood': 'Semáforo: umbral de «bien»',
  'engines.coherence.semaphoreThresholdWarn': 'Semáforo: umbral de «aviso»',
  'engines.coherence.ideaCoincidenceThresholdPct': 'Umbral de coincidencia de idea (%)',
  'engines.coherence.detectionThresholdPct': 'Umbral de detección (%) (alias obsoleto)',
  'engines.coherence.transcriptionProvider': 'Proveedor de transcripción',
  'engines.coherence.rulesetVersion': 'Reglas de fábrica',
  'engines.coherence.ruleset': 'Reglas gestuales',
  // Identity of the configuration itself.
  interview_analysis_config_id: 'Configuración de análisis',
  gestures_mongo_config_id: 'Documento de configuración de gestos',
  version: 'Versión de la configuración',
  engines_hash: 'Huella de los motores',
});

/**
 * Subtrees compared as one value.
 *
 * All three are free-form: a list of records and two maps keyed by whatever exists. Diffing them
 * key by key turns one decision — «se cambió el catálogo» — into forty rows, and buries whatever
 * else changed in the same save.
 */
const WHOLE_SUBTREES = new Set([
  'engines.gestures.gestureCatalog',
  'engines.coherence.reductionByRule',
  'engines.coherence.ruleset',
]);

export type ConfigChangeKind = 'added' | 'removed' | 'changed';

export interface ConfigChange {
  path: string;
  label: string;
  kind: ConfigChangeKind;
  before: unknown;
  after: unknown;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/** Flattens a configuration to `{ 'a.b.c': value }`, keeping arrays and the whole subtrees intact. */
export function flattenConfig(
  value: unknown,
  prefix = '',
  out: Record<string, unknown> = {},
): Record<string, unknown> {
  if (!isPlainObject(value)) {
    if (prefix) out[prefix] = value;
    return out;
  }

  // Sorted, so two diffs of the same pair come out in the same order however the snapshots were
  // serialised.
  for (const key of Object.keys(value).sort()) {
    const path = prefix ? `${prefix}.${key}` : key;
    const child = value[key];

    if (isPlainObject(child) && !WHOLE_SUBTREES.has(path)) {
      flattenConfig(child, path, out);
    } else {
      out[path] = child;
    }
  }

  return out;
}

/**
 * A comparable rendering of a value.
 *
 * Objects are serialised with their keys sorted, because key order is not a change: two
 * snapshots written by different code paths differ in order all the time, and a diff that
 * reported it would cry wolf on every comparison.
 */
function stable(value: unknown): string {
  if (value === undefined) return 'undefined';

  if (isPlainObject(value)) {
    const sorted: Record<string, unknown> = {};
    for (const key of Object.keys(value).sort()) sorted[key] = value[key];

    return JSON.stringify(sorted, (_key, nested) =>
      isPlainObject(nested) ? flattenConfig(nested) : nested,
    );
  }

  return JSON.stringify(value) ?? 'undefined';
}

export function configPathLabel(path: string): string {
  return CONFIG_LABELS[path] ?? path;
}

/** Short, single-line rendering of a configuration value for a diff row. */
export function formatConfigValue(value: unknown): string {
  // Absent and null are different facts: one key was not there, the other was there and empty.
  if (value === undefined) return '—';
  if (value === null) return 'sin valor';
  if (typeof value === 'boolean') return value ? 'sí' : 'no';
  if (Array.isArray(value))
    return `${value.length} ${value.length === 1 ? 'elemento' : 'elementos'}`;

  if (isPlainObject(value)) {
    const keys = Object.keys(value);

    return keys.length === 0 ? 'vacío' : `${keys.length} ${keys.length === 1 ? 'clave' : 'claves'}`;
  }

  return String(value);
}

/**
 * The changes between two configuration snapshots, sorted by path.
 *
 * A missing side means everything is «added», which is what a run predating the versioning looks
 * like: it sealed no configuration, so there is nothing to compare against.
 */
export function diffConfigs(
  previous: Record<string, unknown> | null | undefined,
  current: Record<string, unknown> | null | undefined,
): ConfigChange[] {
  const before = flattenConfig(previous ?? {});
  const after = flattenConfig(current ?? {});
  const paths = [...new Set([...Object.keys(before), ...Object.keys(after)])].sort();
  const changes: ConfigChange[] = [];

  for (const path of paths) {
    const hasBefore = Object.hasOwn(before, path);
    const hasAfter = Object.hasOwn(after, path);

    if (hasBefore && hasAfter && stable(before[path]) === stable(after[path])) continue;

    changes.push({
      path,
      label: configPathLabel(path),
      kind: hasBefore && hasAfter ? 'changed' : hasAfter ? 'added' : 'removed',
      before: before[path],
      after: after[path],
    });
  }

  return changes;
}

export interface EngineChange {
  engine: string;
  before: string | null;
  after: string | null;
}

/**
 * Which engine versions differ between two runs.
 *
 * The first question of HU-29 — «el motor de gestos estaba en 2.0.4 y ahora está en 2.1.0» — and
 * the one that decides whether the metric delta beside it means anything at all.
 */
export function diffEngineVersions(
  before: Record<string, string> | null | undefined,
  after: Record<string, string> | null | undefined,
): EngineChange[] {
  const left = before ?? {};
  const right = after ?? {};
  const engines = [...new Set([...Object.keys(left), ...Object.keys(right)])].sort();

  return engines
    .filter((engine) => left[engine] !== right[engine])
    .map((engine) => ({
      engine,
      before: left[engine] ?? null,
      after: right[engine] ?? null,
    }));
}
